// Copy and predicates shared by the default coverage opt-out container and its
// views. Kept here so the container (which decides where an error is shown) and
// the form (which renders it) agree on the wording.

// Optimistic locking. Update and delete both send the `version` the operator
// loaded; the server refuses the write when the row has moved on since then, so
// two concurrent Ops edits can no longer silently overwrite each other.
//
// THE CODE IS AUTHORITATIVE. Both payloads carry `errorCode`, set to
// STALE_VERSION on the stale path only (branched on exception class
// server-side, so it cannot drift when someone rewords the copy) and null on
// success, auth, 404, timeout and every other failure. `errors[0]` still
// carries the human sentence, which is what we display.
export const STALE_VERSION_CODE = 'STALE_VERSION';

// Fallback ONLY - for a response where `errorCode` came back absent or null
// (an older deploy, a trimmed payload) while the message plainly says
// otherwise. Do not promote this back to the primary check: prose matching is
// exactly what `errorCode` was added to replace.
const STALE_VERSION_SIGNALS = [
  'someone else changed',
  'refresh and retry',
  'stale version',
  'version is stale',
  'version mismatch',
  'changed since you loaded',
];

const messageLooksStale = message => {
  const text = String(message || '').toLowerCase();
  return STALE_VERSION_SIGNALS.some(signal => text.includes(signal));
};

// `errorCode` decides. Only when it is missing/null does the message get a
// look, so a non-stale failure that arrives with an explicit code stays on the
// generic error path no matter how it is worded.
export const isStaleVersionError = (errorCode, message) => {
  if (errorCode) return errorCode === STALE_VERSION_CODE;
  return messageLooksStale(message);
};

// Shown inside the edit modal: the operator still has their typed change in
// front of them, so say plainly that it was not written and what to do next.
export const STALE_VERSION_HEADER = 'Your change was not saved';
export const STALE_VERSION_GUIDANCE = 'Someone else changed this opt-out while you had it open, so nothing was written. '
  + 'Refresh to load the current row, then re-apply your change.';

// Shown on the page after a delete: the confirmation dialog is already gone, so
// there is no modal left to carry the message.
export const STALE_VERSION_DELETE_HEADER = 'The opt-out was not deleted';
export const STALE_VERSION_DELETE_GUIDANCE = 'Someone else changed this opt-out before the delete ran, so it is still in place. '
  + 'Refresh the list, check what changed, then delete it again if it is still what you want.';

// ---------------------------------------------------------------------------
// The product picker. It is fed by `defaultCoverageMasterProductsQuery`, i.e.
// only the products that actually have a master (default coverage)
// subscription seeded - NOT the whole catalog. An opt-out naming a product
// with no master is inert: it saves, it shows up in the admin list looking
// live, and it suppresses nothing, because there is no master on that product
// for it to veto. Two products are seeded today out of ~16 in the catalog.
// ---------------------------------------------------------------------------

// Which side of the deal the product's master subscription covers, and
// therefore which branch an opt-out on that product actually hits. Ops needs
// this in front of them when choosing.
export const MASTER_TYPE_LABEL = {
  MASTER_BUYER: 'Buyer master',
  MASTER_SELLER: 'Seller master',
  MSTR_SELLER_PAID: 'Seller-paid master',
};

// An unmapped `masterType` renders raw rather than disappearing: if the server
// starts returning a third kind, Ops should see the unknown value and ask,
// not see a blank where the side belongs.
export const masterTypeLabel = masterType => MASTER_TYPE_LABEL[masterType] || (masterType ? String(masterType) : '');

// Keyed on `productId` because that is what the opt-out row stores; the code
// and name are only the label, and the master side rides along as the option
// description. `productName` is nullable server-side, so the code stands alone
// when it is missing — and `productCode` is nullable too, so the id stands in for it.
export const toProductOptions = masterProducts => (masterProducts || []).map(product => ({
  key: product.productId,
  value: product.productId,
  text: product.productName
    ? `${product.productCode || product.productId} - ${product.productName}`
    : String(product.productCode || product.productId),
  description: masterTypeLabel(product.masterType),
}));

// Loading, FAILED and empty are three different states that all present as an
// empty options array, and they must not read the same. `loading` rules out the
// in-flight case; `failed` rules out the case where the query errored and we
// therefore do not know what is seeded. "Nothing is seeded" is a claim about
// the data, so it may only be made when the query actually came back with data.
//
// This mattered in practice: on an environment whose API did not yet have
// `defaultCoverageMasterProducts`, the query errored, options came back empty,
// and the form told the operator to go seed default coverage on a product - a
// wrong instruction for a wrong diagnosis. See hasMasterProductsError below.
export const hasNoMasterProducts = (productOptions, loading, failed = false) => !loading
  && !failed
  && (productOptions || []).length === 0;

// The other side of that split: the query came back and it came back broken, so
// the picker has nothing to offer AND we cannot say why. Only ever true once the
// query has settled, for the same reason as above.
export const hasMasterProductsError = (failed, loading) => !loading && Boolean(failed);

// Shown in the create form when the query came back with nothing: there is no
// product to offer, so there is no dropdown either. Say why rather than
// showing an empty picker, and keep Create switched off - a row saved here
// would look live in the list and do nothing at all.
export const NO_MASTER_PRODUCTS_HEADER = 'No product has default coverage seeded';
export const NO_MASTER_PRODUCTS_GUIDANCE = 'No product has default (master) coverage seeded yet, '
  + 'so an opt-out would have nothing to suppress - the row would look live in the list and do nothing. '
  + 'Seed default coverage on the product first, then create the opt-out.';

// Shown instead of the above when the products query FAILED. Deliberately does
// not tell the operator to seed anything: we did not get an answer, so we do
// not know whether anything is seeded, and sending them to seed a product they
// may already have is a worse outcome than saying plainly that the load broke.
// Create stays blocked either way - a row saved without a real product list is
// a guess.
export const MASTER_PRODUCTS_ERROR_HEADER = 'Could not load the products with default coverage';
export const MASTER_PRODUCTS_ERROR_GUIDANCE = 'The list of products with default (master) coverage did not load, '
  + 'so there is nothing to choose from and no way to tell what is seeded. This is a load failure, not a sign that '
  + 'nothing is seeded. Reload the page; if it keeps failing the API may not have this feature deployed yet, so '
  + 'check with the team before seeding anything.';

// Shown in the create form's help box whenever the picker is usable: the list
// is the seeded products only, which is exactly why every row saved from it
// can actually suppress something.
// Masters exist, but none on the side the operator picked. Distinct from
// "nothing is seeded anywhere": the fix is to switch sides or add a master on
// THIS side, not to go seed default coverage from scratch. Without this the
// picker just renders empty and Create fails with "select at least one product",
// which is advice the operator cannot act on.
export const NO_PRODUCTS_ON_SIDE_HEADER = 'No default coverage on this party side';
export const NO_PRODUCTS_ON_SIDE_GUIDANCE = 'No default coverage for this party side setup yet - '
  + 'Switch to the other side OR add a master for this side in Default Coverage Masters first.';

export const MASTER_PRODUCTS_ONLY_GUIDANCE = 'The list offers only products that have default (master) coverage seeded on them. '
  + 'An opt-out naming a product without a master is inert - it is saved and looks live in the list, but suppresses nothing, '
  + 'because there is no master on that product to veto.';

/**
 * Says out loud what ticking every row means, because the picker does not look like
 * a switch that turns coverage off — it looks like a list of products. An operator
 * reaching for "Select all" is one click from leaving a party with nothing.
 */
export const SELECT_ALL_GUIDANCE = 'Ticking every product — or using Select all — leaves that party with NO default '
  + 'coverage at all: every master on this side is suppressed, so a qualifying sale creates no order and nobody is '
  + 'charged. Their own individual subscription, if they have one, is unaffected and still applies.';

// ---------------------------------------------------------------------------
// Multi-select picker
//
// One party, many products, one submit. The screen went multi-select because a
// suppression is per (party, product) and there is now more than one master per
// side: fully excluding a party means one row per product, so making that N
// separate visits to a modal was the wrong shape.
// ---------------------------------------------------------------------------

// Three Party Side radios (v2), matching Masters' Payer Side — but only two real
// backend branches. The buyer branch (buyerNumber and/or auctionAccessNumber) only
// ever suppresses a MASTER_BUYER product. Seller and seller-paid BOTH submit
// sellerNumber alone — the same column, the same isSuppressed call — and differ
// only in which products this map offers, never in what gets sent. See
// DefaultCoverageServiceImpl#resolveMaster: the non-buyer branch's isSuppressed call
// never reads auctionAccessNumber, and MASTER_SELLER / MSTR_SELLER_PAID both route
// through it identically.
export const MASTER_TYPES_FOR_SIDE = {
  buyer: ['MASTER_BUYER'],
  seller: ['MASTER_SELLER'],
  seller_paid: ['MSTR_SELLER_PAID'],
};

/**
 * The master products that a suppression on THIS party side can actually affect.
 *
 * Fixes a real defect: the picker used to offer every seeded master regardless of
 * side, so Party Side = Buyer with a MASTER_SELLER product was savable. The buyer
 * branch only ever resolves MASTER_BUYER, so that row was stored, shown as live in
 * the admin list, and could never suppress anything — the exact silent no-op the
 * filtered picker exists to prevent.
 */
export const productsForPartySide = (masterProducts, partySide) => {
  const wanted = MASTER_TYPES_FOR_SIDE[partySide];
  if (!wanted) return [];
  return (masterProducts || []).filter(product => product && wanted.includes(product.masterType));
};

/**
 * Products this party is ALREADY opted out of, so the picker can pre-check them.
 *
 * Matched on the SAME shape the create form would submit — buyerNumber and/or
 * auctionAccessNumber together, or sellerNumber alone — because that shape is what
 * APMS's duplicate guard matches on (v2): `(buyerNumber, null)`, `(null, aan)` and
 * `(buyerNumber, aan)` are three different vetoes, not the same party three ways.
 */
export const alreadyOptedOutProductIds = (existingOptOuts, { buyerNumber, sellerNumber, auctionAccessNumber } = {}) => {
  const buyer = String(buyerNumber || '').trim();
  const seller = String(sellerNumber || '').trim();
  const aan = String(auctionAccessNumber || '').trim();
  if (!buyer && !seller && !aan) return [];

  return (existingOptOuts || [])
    .filter(row => row && (
      seller
        ? String(row.sellerNumber || '') === seller
        : String(row.buyerNumber || '') === buyer && String(row.auctionAccessNumber || '') === aan
    ))
    .map(row => String(row.productId));
};

/**
 * One create input per product. Start date, end date and notes are SHARED across the
 * batch: one operator action is one reason and one date window, and N date pickers
 * would be a worse form for something nobody asks for. Per-row edits stay on the Edit
 * screen.
 *
 * Buyer-side rows carry buyerNumber and/or auctionAccessNumber (v2's three legal
 * buyer-branch shapes); seller-side rows carry sellerNumber alone.
 */
export const buildCreateInputs = ({
  partySide, buyerNumber, sellerNumber, auctionAccessNumber, productIds, startDate, endDate, notes,
}) => {
  const isBuyer = partySide === 'buyer';
  const buyer = String(buyerNumber || '').trim() || null;
  const seller = String(sellerNumber || '').trim() || null;
  const aan = String(auctionAccessNumber || '').trim() || null;

  return (productIds || []).map(productId => ({
    defaultCoverageOptOut: {
      buyerNumber: isBuyer ? buyer : null,
      sellerNumber: isBuyer ? null : seller,
      auctionAccessNumber: isBuyer ? aan : null,
      productId,
      startDate: startDate || null,
      endDate: endDate || null,
      notes: notes || null,
    },
  }));
};

/**
 * Turns N mutation outcomes into one legible result.
 *
 * A batch can partially succeed, and "Error while creating" over the whole thing
 * would hide which products landed. An already-existing row is a NO-OP, not a
 * failure: re-submitting a party whose first product was already opted out is the
 * normal way to add the rest, and it must not read as an error.
 */
export const summariseCreateResults = results => {
  const created = [];
  const alreadyPresent = [];
  const failed = [];

  (results || []).forEach(result => {
    if (!result) return;
    const { productCode, success, error } = result;
    if (success) {
      created.push(productCode);
    } else if (error && /already exists/i.test(error)) {
      alreadyPresent.push(productCode);
    } else {
      failed.push({ productCode, error });
    }
  });

  return {
    created, alreadyPresent, failed, allSucceeded: failed.length === 0,
  };
};

export const describeCreateResults = ({ created, alreadyPresent, failed }) => {
  const parts = [];
  if (created.length > 0) parts.push(`Opted out of ${created.join(', ')}`);
  if (alreadyPresent.length > 0) parts.push(`already opted out of ${alreadyPresent.join(', ')}`);
  if (failed.length > 0) {
    parts.push(`failed for ${failed.map(f => `${f.productCode} (${f.error})`).join(', ')}`);
  }
  if (parts.length === 0) return '';
  // The clauses are written to read as continuations ("Opted out of A; already opted
  // out of B."), which left the sentence starting lower-case whenever nothing was
  // created - the operator saw the bare fragment "already opted out of X."
  const sentence = parts.join('; ');
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
};

/**
 * Whether the flash for this outcome should read as a success.
 *
 * An already-existing row is not a failure - the desired end state holds - but it is
 * not an achievement either, and showing a green "success" for a submit that wrote
 * nothing told the operator their click had landed when it had not. Positive only
 * when something was actually created.
 */
export const createdSomething = ({ created }) => (created || []).length > 0;
